/**
 * QA #21 — Wishlist (Swish "Wishlist King" / Appmate) headless button wiring.
 *
 * Wishlist King runs in HEADLESS mode: at runtime it stamps the theme's wishlist
 * buttons with `wk-headless` / `wk-skip` and attaches NO click behaviour — the
 * theme is expected to drive them through `window.WishlistKing.*`. The theme
 * renders the buttons (snippets/swish-wishlist-button.liquid) but never wired
 * them, so clicks did nothing and the bookmark never filled in.
 *
 * API (verified live against @appmate/wishlist):
 *   read   -> WishlistKing._apis.wishlist.loadWishlist({ wishlistId: 'mine' })
 *              -> { wishlist: { items: [{ id, product: { handle } }] } }
 *   add    -> WishlistKing.addWishlistItem({ productHandle, selectedVariantId })
 *              -> { wishlistItem: { id } }
 *   remove -> WishlistKing.removeWishlistItem({ wishlistItemId })   // NB: the key
 *              is `wishlistItemId`, not `id`/`productHandle` — the wrong key sends
 *              an empty DELETE body and the API 400s.
 *
 * We own an `is-wishlisted` class; the icon fill is forced from it in
 * by-wren-reskin.css (with !important, so it beats Wishlist King's own sticky
 * `wk-selected` fill). Initial state + the id-for-removal come from loadWishlist.
 *
 * Clicks are delegated (capture) so buttons injected later — cart drawer / quick
 * view after an AJAX re-render — work too, and state re-applies on those renders.
 */
(function () {
  var SELECTOR = '.wkh-button[data-product-handle]';
  var ACTIVE = 'is-wishlisted';
  var ids = new Map();        // product handle -> wishlistItemId
  var interacted = new Set(); // handles toggled by the user (don't let hydrate flip them)

  function wk() { return window.WishlistKing; }
  function esc(h) { return window.CSS && CSS.escape ? CSS.escape(h) : h.replace(/"/g, '\\"'); }

  function setActive(handle, on) {
    document.querySelectorAll('.wkh-button[data-product-handle="' + esc(handle) + '"]').forEach(function (b) {
      b.classList.toggle(ACTIVE, on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function readWishlist() {
    var api = wk();
    if (!api || !api._apis || !api._apis.wishlist) return Promise.resolve(null);
    return Promise.resolve(api._apis.wishlist.loadWishlist({ wishlistId: 'mine' })).catch(function () { return null; });
  }

  function indexItems(wl) {
    var items = (wl && wl.wishlist && wl.wishlist.items) || (wl && wl.items) || [];
    items.forEach(function (i) { var h = i.product && i.product.handle; if (h) ids.set(h, i.id); });
    return items.length;
  }

  function toggle(btn) {
    var api = wk();
    var handle = btn.dataset.productHandle;
    if (!api || !handle || btn.dataset.wkBusy === '1') return;
    interacted.add(handle);

    var isOn = btn.classList.contains(ACTIVE);
    btn.dataset.wkBusy = '1';
    btn.classList.add('is-loading');

    var op;
    if (isOn) {
      // Need the wishlistItemId. Use the cached one, else read the wishlist to find it.
      op = (ids.has(handle) ? Promise.resolve(ids.get(handle)) : readWishlist().then(function (wl) { indexItems(wl); return ids.get(handle); }))
        .then(function (itemId) {
          if (!itemId) throw new Error('no wishlistItemId for ' + handle);
          return api.removeWishlistItem({ wishlistItemId: itemId });
        })
        .then(function () { ids.delete(handle); });
    } else {
      var variantId = btn.dataset.variantId ? Number(btn.dataset.variantId) : null;
      op = api.addWishlistItem({ productHandle: handle, selectedVariantId: variantId })
        .then(function (res) { if (res && res.wishlistItem) ids.set(handle, res.wishlistItem.id); });
    }

    Promise.resolve(op)
      .then(function () { setActive(handle, !isOn); })
      .catch(function (e) { console.error('[wishlist] toggle failed', e); })
      .then(function () { delete btn.dataset.wkBusy; btn.classList.remove('is-loading'); });
  }

  // Delegated + capture so it wins over the card's link and covers buttons that
  // are rendered after load (cart drawer, quick view).
  document.addEventListener('click', function (e) {
    var btn = e.target.closest(SELECTOR);
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    toggle(btn);
  }, true);

  // Load the wishlist once, map handle -> wishlistItemId, and mark saved buttons.
  function hydrate() {
    readWishlist().then(function (wl) {
      if (!wl) return;
      indexItems(wl);
      document.querySelectorAll(SELECTOR).forEach(function (b) {
        var handle = b.dataset.productHandle;
        if (interacted.has(handle)) return;
        if (ids.has(handle)) { b.classList.add(ACTIVE); b.setAttribute('aria-pressed', 'true'); }
      });
    });
  }

  function ready(cb) {
    if (wk() && wk()._apis) return cb();
    var n = 0;
    var t = setInterval(function () {
      if (wk() && wk()._apis) { clearInterval(t); cb(); }
      else if (++n > 50) clearInterval(t);
    }, 200);
  }

  ready(function () {
    hydrate();
    document.addEventListener('cart-drawer:open', hydrate);
    document.addEventListener('cart:refresh', hydrate);
  });
})();
